import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit2, X, Save, Eye, EyeOff, Image, Film, ExternalLink, Upload, Loader2 } from 'lucide-react';
import FileUploader from './FileUploader';
import { getImageUrl } from '../../lib/utils';

// Video Uploader Component
const VideoUploader = ({ onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('video/')) {
      alert('Lütfen bir video dosyası seçin');
      return;
    }

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert('Video dosyası 100MB\'dan küçük olmalı');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          const fullUrl = `${backendUrl}${data.url}`;
          onUpload(fullUrl);
        } else {
          alert('Video yükleme başarısız');
        }
        setUploading(false);
        setProgress(0);
      });

      xhr.addEventListener('error', () => {
        alert('Video yükleme hatası');
        setUploading(false);
        setProgress(0);
      });

      xhr.open('POST', `${backendUrl}/api/admin/upload`);
      xhr.send(formData);

    } catch (error) {
      console.error('Video upload error:', error);
      alert('Video yükleme hatası');
      setUploading(false);
      setProgress(0);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="px-3 py-2 border hover:bg-gray-50 text-sm flex items-center gap-1 disabled:opacity-50"
        title="Video Yükle"
      >
        {uploading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            <span className="text-xs">{progress}%</span>
          </>
        ) : (
          <Upload size={16} />
        )}
      </button>
    </>
  );
};

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    cover_image: '',
    images: [],
    video_url: '',
    tags: '',
    is_published: false
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/blog`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPost = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      cover_image: post.cover_image || '',
      images: post.images || [],
      video_url: post.video_url || '',
      tags: (post.tags || []).join(', '),
      is_published: post.is_published || false
    });
    setEditMode(false);
    setMessage('');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      cover_image: '',
      images: [],
      video_url: '',
      tags: '',
      is_published: false
    });
  };

  const handleCreatePost = async () => {
    if (!formData.title || !formData.content) {
      setMessage('Başlık ve içerik zorunludur');
      return;
    }

    setSaving(true);
    try {
      const postData = {
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content,
        cover_image: formData.cover_image || null,
        images: formData.images,
        video_url: formData.video_url || null,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        is_published: formData.is_published
      };

      const res = await fetch(`${backendUrl}/api/admin/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });

      if (res.ok) {
        await fetchPosts();
        setShowCreateModal(false);
        resetForm();
        setMessage('✓ Blog yazısı oluşturuldu');
      } else {
        const error = await res.json();
        setMessage(`Hata: ${error.detail || 'Yazı oluşturulamadı'}`);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setMessage('Hata: Yazı oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePost = async () => {
    if (!selectedPost) return;

    setSaving(true);
    try {
      const postData = {
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content,
        cover_image: formData.cover_image || null,
        images: formData.images,
        video_url: formData.video_url || null,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        is_published: formData.is_published
      };

      const res = await fetch(`${backendUrl}/api/admin/blog/${selectedPost.post_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });

      if (res.ok) {
        await fetchPosts();
        setEditMode(false);
        setMessage('✓ Blog yazısı güncellendi');
        
        // Refresh selected post
        const updatedPost = posts.find(p => p.post_id === selectedPost.post_id);
        if (updatedPost) {
          setSelectedPost({ ...updatedPost, ...postData });
        }
      } else {
        const error = await res.json();
        setMessage(`Hata: ${error.detail || 'Güncelleme başarısız'}`);
      }
    } catch (error) {
      console.error('Error updating post:', error);
      setMessage('Hata: Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) return;

    try {
      const res = await fetch(`${backendUrl}/api/admin/blog/${postId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        await fetchPosts();
        if (selectedPost?.post_id === postId) {
          setSelectedPost(null);
        }
        setMessage('✓ Blog yazısı silindi');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      setMessage('Hata: Silme başarısız');
    }
  };

  const handleTogglePublish = async (postId) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/blog/${postId}/publish`, {
        method: 'POST'
      });

      if (res.ok) {
        const result = await res.json();
        await fetchPosts();
        setMessage(`✓ ${result.message}`);
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const handleAddImage = (url) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, url]
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Blog Yönetimi</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{posts.length} yazı</span>
          <button
            onClick={() => { setShowCreateModal(true); resetForm(); }}
            className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800"
          >
            <Plus size={16} />
            Yeni Yazı
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('✓') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Posts List */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-4">Blog Yazıları</h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {posts.map((post) => (
              <div
                key={post.post_id}
                onClick={() => handleSelectPost(post)}
                className={`p-3 border rounded cursor-pointer transition-colors ${
                  selectedPost?.post_id === post.post_id ? 'border-black bg-gray-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {post.cover_image ? (
                    <img
                      src={getImageUrl(post.cover_image)}
                      alt={post.title}
                      className="w-16 h-12 rounded object-cover flex-shrink-0 bg-gray-100"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/64x48?text=V'; }}
                    />
                  ) : (
                    <div className="w-16 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-400 text-xs">V</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{post.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${post.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {post.is_published ? 'Yayında' : 'Taslak'}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {posts.length === 0 && (
              <p className="text-center text-gray-500 py-8">Henüz blog yazısı yok</p>
            )}
          </div>
        </div>

        {/* Post Details / Edit Form */}
        <div className="lg:col-span-2 bg-white border rounded-lg p-4">
          {selectedPost ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">
                  {editMode ? 'Yazıyı Düzenle' : 'Yazı Detayları'}
                </h3>
                <div className="flex items-center gap-2">
                  {editMode ? (
                    <>
                      <button
                        onClick={() => {
                          setEditMode(false);
                          handleSelectPost(selectedPost);
                        }}
                        className="px-3 py-1 text-sm border hover:bg-gray-50"
                      >
                        İptal
                      </button>
                      <button
                        onClick={handleUpdatePost}
                        disabled={saving}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 disabled:bg-gray-400"
                      >
                        <Save size={14} />
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleTogglePublish(selectedPost.post_id)}
                        className={`flex items-center gap-1 px-3 py-1 text-sm border ${selectedPost.is_published ? 'text-yellow-600' : 'text-green-600'}`}
                      >
                        {selectedPost.is_published ? <EyeOff size={14} /> : <Eye size={14} />}
                        {selectedPost.is_published ? 'Yayından Kaldır' : 'Yayınla'}
                      </button>
                      <button
                        onClick={() => setEditMode(true)}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800"
                      >
                        <Edit2 size={14} />
                        Düzenle
                      </button>
                      <button
                        onClick={() => handleDeletePost(selectedPost.post_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {editMode ? (
                <BlogForm 
                  formData={formData} 
                  setFormData={setFormData}
                  onAddImage={handleAddImage}
                  onRemoveImage={handleRemoveImage}
                />
              ) : (
                <div className="space-y-4 max-h-[450px] overflow-y-auto">
                  {selectedPost.cover_image && (
                    <img
                      src={getImageUrl(selectedPost.cover_image)}
                      alt={selectedPost.title}
                      className="w-full h-48 object-cover rounded bg-gray-100"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/400x200?text=VALMÉRA'; }}
                    />
                  )}
                  
                  <div>
                    <h4 className="text-xl font-semibold">{selectedPost.title}</h4>
                    <p className="text-gray-500 text-sm mt-1">{selectedPost.excerpt}</p>
                  </div>
                  
                  <div className="flex gap-4 text-sm">
                    <span className="text-gray-500">
                      Oluşturulma: {formatDate(selectedPost.created_at)}
                    </span>
                    <span className="text-gray-500">
                      Görüntüleme: {selectedPost.view_count || 0}
                    </span>
                  </div>
                  
                  {selectedPost.tags && selectedPost.tags.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {selectedPost.tags.map((tag) => (
                        <span key={tag} className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-500 mb-2">İçerik:</p>
                    <div className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded max-h-40 overflow-y-auto">
                      {selectedPost.content}
                    </div>
                  </div>
                  
                  {selectedPost.is_published && (
                    <a
                      href={`/blog/${selectedPost.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink size={14} />
                      Yazıyı Görüntüle
                    </a>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Düzenlemek için bir yazı seçin
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Yeni Blog Yazısı</h3>
              <button onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <BlogForm 
                formData={formData} 
                setFormData={setFormData}
                onAddImage={handleAddImage}
                onRemoveImage={handleRemoveImage}
              />
              
              <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm border hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  onClick={handleCreatePost}
                  disabled={!formData.title || !formData.content || saving}
                  className="px-4 py-2 text-sm bg-black text-white hover:bg-gray-800 disabled:bg-gray-300"
                >
                  {saving ? 'Oluşturuluyor...' : 'Oluştur'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Blog Form Component
const BlogForm = ({ formData, setFormData, onAddImage, onRemoveImage }) => {
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [imageUploadType, setImageUploadType] = useState('cover'); // 'cover' or 'gallery'

  return (
    <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">Başlık *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full border px-3 py-2 text-sm rounded"
          placeholder="Blog yazısı başlığı"
        />
      </div>
      
      {/* Excerpt */}
      <div>
        <label className="block text-sm font-medium mb-1">Özet</label>
        <textarea
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
          rows={2}
          className="w-full border px-3 py-2 text-sm rounded"
          placeholder="Kısa açıklama (listede görünür)"
        />
      </div>
      
      {/* Content */}
      <div>
        <label className="block text-sm font-medium mb-1">İçerik *</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={8}
          className="w-full border px-3 py-2 text-sm rounded font-mono"
          placeholder="Blog yazısı içeriği..."
        />
      </div>
      
      {/* Cover Image */}
      <div>
        <label className="block text-sm font-medium mb-1">Kapak Görseli</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={formData.cover_image}
            onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
            className="flex-1 border px-3 py-2 text-sm rounded"
            placeholder="https://... veya dosya yükle"
          />
          <button
            onClick={() => { setImageUploadType('cover'); setShowImageUploader(true); }}
            className="px-3 py-2 border hover:bg-gray-50 text-sm"
          >
            <Image size={16} />
          </button>
        </div>
        {formData.cover_image && (
          <img 
            src={getImageUrl(formData.cover_image)} 
            alt="Cover" 
            className="mt-2 h-24 object-cover rounded bg-gray-100"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/200x100?text=V'; }}
          />
        )}
      </div>
      
      {/* Video URL */}
      <div>
        <label className="block text-sm font-medium mb-1">Video</label>
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <Film size={16} className="text-gray-400" />
            <input
              type="text"
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              className="flex-1 border px-3 py-2 text-sm rounded"
              placeholder="Video URL veya dosya yükle"
            />
            <VideoUploader 
              onUpload={(url) => setFormData({ ...formData, video_url: url })}
            />
          </div>
          {formData.video_url && (
            <div className="relative">
              <video 
                src={getImageUrl(formData.video_url)} 
                className="w-full h-32 object-cover rounded bg-black"
                controls
              />
              <button
                onClick={() => setFormData({ ...formData, video_url: '' })}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Gallery Images */}
      <div>
        <label className="block text-sm font-medium mb-1">Galeri Görselleri</label>
        <button
          onClick={() => { setImageUploadType('gallery'); setShowImageUploader(true); }}
          className="flex items-center gap-2 px-3 py-2 border hover:bg-gray-50 text-sm mb-2"
        >
          <Plus size={14} />
          Görsel Ekle
        </button>
        {formData.images.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {formData.images.map((img, idx) => (
              <div key={idx} className="relative group">
                <img 
                  src={getImageUrl(img)} 
                  alt={`Gallery ${idx}`} 
                  className="w-16 h-16 object-cover rounded bg-gray-100"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/64'; }}
                />
                <button
                  onClick={() => onRemoveImage(idx)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Tags */}
      <div>
        <label className="block text-sm font-medium mb-1">Etiketler</label>
        <input
          type="text"
          value={formData.tags}
          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          className="w-full border px-3 py-2 text-sm rounded"
          placeholder="İpek, Bakım, Stil (virgülle ayırın)"
        />
      </div>
      
      {/* Publish Status */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_published"
          checked={formData.is_published}
          onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
          className="rounded"
        />
        <label htmlFor="is_published" className="text-sm">
          Hemen yayınla
        </label>
      </div>

      {/* Image Upload Modal */}
      {showImageUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">
                {imageUploadType === 'cover' ? 'Kapak Görseli' : 'Galeri Görseli'}
              </h4>
              <button onClick={() => setShowImageUploader(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">URL ile</label>
                <input
                  type="text"
                  placeholder="https://..."
                  className="w-full border px-3 py-2 text-sm rounded"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      if (imageUploadType === 'cover') {
                        setFormData({ ...formData, cover_image: e.target.value });
                      } else {
                        onAddImage(e.target.value);
                      }
                      setShowImageUploader(false);
                    }
                  }}
                />
              </div>
              
              <div className="border-t pt-4">
                <label className="block text-sm mb-2">veya Dosya Yükle</label>
                <FileUploader
                  accept="image/*"
                  label="Görsel Seç"
                  onUpload={(url) => {
                    if (imageUploadType === 'cover') {
                      setFormData({ ...formData, cover_image: url });
                    } else {
                      onAddImage(url);
                    }
                    setShowImageUploader(false);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
